import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Building2, Menu, X, ChevronDown, Globe } from 'lucide-react';

export const Header = ({ onContactClick }) => {
    const { t, i18n } = useTranslation();
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const languages = [
        { code: 'en', name: 'English', flag: '🇬🇧' },
        { code: 'es', name: 'Español', flag: '🇪🇸' },
        { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
        { code: 'fr', name: 'Français', flag: '🇫🇷' },
        { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    ];

    const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

    const handleLanguageChange = (langCode) => {
        i18n.changeLanguage(langCode);
    };

    const handleNavClick = (href, e) => {
        if (href === '#contact') {
            e.preventDefault();
            onContactClick?.();
            setIsMobileMenuOpen(false);
        } else {
            setIsMobileMenuOpen(false);
        }
    };

    const navLinks = [
        { href: '#simulator', label: t('nav.loanSimulator') },
        { href: '#about', label: t('nav.aboutUs') },
        { href: '#contact', label: t('nav.contact') },
    ];

    return (
        <header
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                isScrolled || isMobileMenuOpen
                    ? 'bg-background/95 backdrop-blur-md shadow-md py-3'
                    : 'bg-transparent py-5'
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                <div className="flex items-center justify-between">
                    {/* Logo */}
                    <a href="/" className="flex items-center gap-3 group">
                        <div className="relative">
                            <img 
                                src="https://customer-assets.emergentagent.com/job_modern-spilka-1/artifacts/xb96a3rm_imagen.png" 
                                alt="Spilka Consulting Logo"
                                className="w-10 h-10 object-contain group-hover:scale-105 transition-transform duration-300"
                                style={{ filter: 'hue-rotate(120deg) saturate(1.2)' }}
                            />
                        </div>
                        <span className="font-display text-xl font-semibold text-foreground tracking-tight">
                            SPILKA <span className="text-primary">CONSULTING</span>
                        </span>
                    </a>

                    {/* Desktop Navigation */}
                    <nav className="hidden lg:flex items-center gap-8">
                        {navLinks.map((link) => (
                            <a
                                key={link.href}
                                href={link.href}
                                onClick={(e) => handleNavClick(link.href, e)}
                                className="text-muted-foreground hover:text-primary font-medium text-sm link-underline py-1 transition-colors duration-200 cursor-pointer"
                            >
                                {link.label}
                            </a>
                        ))}
                    </nav>

                    {/* Right Side Actions */}
                    <div className="flex items-center gap-4">
                        {/* Real Estate Properties Button */}
                        <a
                            href="https://www.casaconsulting.ch"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hidden sm:block"
                        >
                            <Button className="btn-gold px-5 py-2 rounded-full text-sm font-medium">
                                <Building2 className="w-4 h-4 mr-2" />
                                {t('nav.realEstate')}
                            </Button>
                        </a>

                        {/* Language Selector */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="ghost"
                                    className="hidden md:flex items-center gap-2 text-muted-foreground hover:text-foreground"
                                >
                                    <Globe className="w-4 h-4" />
                                    <span className="text-sm">{currentLang.name}</span>
                                    <ChevronDown className="w-3 h-3" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-40">
                                {languages.map((lang) => (
                                    <DropdownMenuItem
                                        key={lang.code}
                                        onClick={() => handleLanguageChange(lang.code)}
                                        className={`cursor-pointer ${lang.code === i18n.language ? 'bg-primary/10 text-primary' : ''}`}
                                    >
                                        <span className="mr-2">{lang.flag}</span>
                                        {lang.name}
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>

                        {/* Mobile Menu Button */}
                        <Button
                            variant="ghost"
                            size="icon"
                            className="lg:hidden"
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        >
                            {isMobileMenuOpen ? (
                                <X className="w-5 h-5" />
                            ) : (
                                <Menu className="w-5 h-5" />
                            )}
                        </Button>
                    </div>
                </div>

                {/* Mobile Menu Overlay */}
                {isMobileMenuOpen && (
                    <div className="lg:hidden absolute top-full left-0 right-0 bg-background shadow-lg border-t border-border animate-slide-down">
                        <nav className="flex flex-col gap-1 p-4">
                            {navLinks.map((link) => (
                                <a
                                    key={link.href}
                                    href={link.href}
                                    className="text-foreground hover:text-primary hover:bg-primary/5 font-medium text-sm py-3 px-4 rounded-lg transition-colors cursor-pointer"
                                    onClick={(e) => handleNavClick(link.href, e)}
                                >
                                    {link.label}
                                </a>
                            ))}
                            
                            {/* Mobile Language Selector */}
                            <div className="py-2 px-4">
                                <p className="text-xs text-muted-foreground mb-2">Language</p>
                                <div className="flex flex-wrap gap-2">
                                    {languages.map((lang) => (
                                        <button
                                            key={lang.code}
                                            onClick={() => handleLanguageChange(lang.code)}
                                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                                                lang.code === i18n.language 
                                                    ? 'bg-primary text-primary-foreground' 
                                                    : 'bg-muted text-muted-foreground hover:bg-primary/10'
                                            }`}
                                        >
                                            {lang.flag} {lang.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            
                            <div className="pt-3 mt-2 border-t border-border">
                                <a
                                    href="https://www.casaconsulting.ch"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                >
                                    <Button className="btn-gold w-full rounded-full">
                                        <Building2 className="w-4 h-4 mr-2" />
                                        {t('nav.realEstate')}
                                    </Button>
                                </a>
                            </div>
                        </nav>
                    </div>
                )}
            </div>
        </header>
    );
};
